import React from 'react';
import { Link, Navigate, type RouteObject } from 'react-router-dom';
import { RootLayout } from '../routes/RootLayout';
import { RouteErrorBoundary } from '../boundaries/RouteErrorBoundary';
import { Button } from '@/components/ui/button';
import { ErrorPanel } from '@/components/ErrorPanel';

// BrowsePage is intentionally eager — it's the home route ("/") and on
// the mobile LCP critical path. Lazy-loading it adds an extra chunk
// resolution + module-registration round-trip before the hero <img>
// can render. See plan 2026-05-11-perf-round-3-render-path.md Lever B.
import { BrowsePage } from '../routes/BrowsePage';

/* ------------------------------------------------------------------ */
/*  Route-level code-splitting via React.lazy                         */
/*  Named exports require the .then(m => ({ default: m.X })) pattern  */
/* ------------------------------------------------------------------ */
const PackDetailPage = React.lazy(() =>
  import('../routes/PackDetailPage').then(m => ({ default: m.PackDetailPage })),
);
const CheckoutPage = React.lazy(() =>
  import('../routes/CheckoutPage').then(m => ({ default: m.CheckoutPage })),
);
const OrderSuccessPage = React.lazy(() =>
  import('../routes/OrderSuccessPage').then(m => ({ default: m.OrderSuccessPage })),
);
// OrdersPage removed — orders are now under /account?tab=orders (Phase A)
const SignInPage = React.lazy(() =>
  import('../routes/SignInPage').then(m => ({ default: m.SignInPage })),
);
const MarketplacePage = React.lazy(() =>
  import('../routes/MarketplacePage').then(m => ({ default: m.MarketplacePage })),
);
const MarketplacePurchasesPage = React.lazy(() =>
  import('../routes/MarketplacePurchasesPage').then(m => ({
    default: m.MarketplacePurchasesPage,
  })),
);
const AccountPage = React.lazy(() =>
  import('../routes/AccountPage').then(m => ({ default: m.AccountPage })),
);
const AdminAuthGuard = React.lazy(() =>
  import('../routes/admin/AdminAuthGuard').then(m => ({ default: m.AdminAuthGuard })),
);
const AdminLogin = React.lazy(() =>
  import('../routes/admin/AdminLogin').then(m => ({ default: m.AdminLogin })),
);
const AdminDashboard = React.lazy(() =>
  import('../routes/admin/AdminDashboard').then(m => ({ default: m.AdminDashboard })),
);
const AdminOrderDetail = React.lazy(() =>
  import('../routes/admin/AdminOrderDetail').then(m => ({
    default: m.AdminOrderDetail,
  })),
);
const AdminPackWeights = React.lazy(() =>
  import('../routes/admin/AdminPackWeights').then(m => ({ default: m.AdminPackWeights })),
);
const AdminAnalytics = React.lazy(() =>
  import('../routes/admin/AdminAnalytics').then(m => ({ default: m.AdminAnalytics })),
);
const AdminFeedback = React.lazy(() =>
  import('../routes/admin/AdminFeedback').then(m => ({ default: m.AdminFeedback })),
);
const FulfillmentPage = React.lazy(() =>
  import('../pages/admin/FulfillmentPage').then(m => ({ default: m.FulfillmentPage })),
);
const AdminLayout = React.lazy(() =>
  import('../components/admin/AdminLayout').then((m) => ({ default: m.AdminLayout })),
);
const WelcomePage = React.lazy(() =>
  import('../routes/WelcomePage').then((m) => ({ default: m.WelcomePage })),
);
const GiftClaimPage = React.lazy(() =>
  import('../routes/GiftClaimPage').then(m => ({ default: m.GiftClaimPage })),
);
const TermsPage = React.lazy(() =>
  import('../routes/TermsPage').then(m => ({ default: m.TermsPage })),
);
const AboutPage = React.lazy(() =>
  import('../routes/AboutPage').then(m => ({ default: m.AboutPage })),
);
/**
 * Pullit route tree.
 *
 * Per frontend.md §15.1:
 *   /                     Pack browse (public)
 *   /pack/:pack_id        Pack detail (public — purchase requires auth)
 *   /sign-in              Cognito sign-in / sign-up
 *   /orders               Customer order history (Phase 3 stub in Phase 1)
 *
 * Every route element is wrapped in the RouteErrorBoundary via
 * `errorElement` per frontend.md §15.4.
 */

function NotFoundPage(): JSX.Element {
  return (
    <ErrorPanel
      title="404 — Page not found"
      message="We couldn't find that page."
      actions={
        <Button asChild variant="outline">
          <Link to="/" className="no-underline">
            Back to browse
          </Link>
        </Button>
      }
    />
  );
}

export const appRoutes: RouteObject[] = [
  {
    path: '/',
    element: <RootLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <BrowsePage />,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'pack/:pack_id',
        element: <PackDetailPage />,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'pack/:pack_id/checkout',
        element: <CheckoutPage />,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'sign-in',
        element: <SignInPage />,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'welcome',
        element: <WelcomePage />,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'marketplace',
        element: <MarketplacePage />,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'marketplace/purchases',
        element: <MarketplacePurchasesPage />,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'account',
        element: <AccountPage />,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'orders',
        element: <Navigate to="/account?tab=orders" replace />,
      },
      {
        path: 'orders/:order_id',
        element: <OrderSuccessPage />,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'terms',
        element: <TermsPage />,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'about',
        element: <AboutPage />,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'gift/:gift_id',
        element: <GiftClaimPage />,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'admin/login',
        element: <AdminLogin />,
        errorElement: <RouteErrorBoundary />,
      },
      {
        path: 'admin',
        element: <AdminAuthGuard />,
        errorElement: <RouteErrorBoundary />,
        children: [
          {
            index: true,
            element: <Navigate to="dashboard" replace />,
          },
          {
            element: <AdminLayout />,
            children: [
              {
                path: 'dashboard',
                element: <AdminDashboard />,
                errorElement: <RouteErrorBoundary />,
              },
              {
                path: 'fulfillment',
                element: <FulfillmentPage />,
                errorElement: <RouteErrorBoundary />,
              },
              {
                path: 'shipments/:shipment_id',
                element: <AdminOrderDetail />,
                errorElement: <RouteErrorBoundary />,
              },
              {
                path: 'pack-weights',
                element: <AdminPackWeights />,
                errorElement: <RouteErrorBoundary />,
              },
              {
                path: 'analytics',
                element: <AdminAnalytics />,
                errorElement: <RouteErrorBoundary />,
              },
              {
                path: 'feedback',
                element: <AdminFeedback />,
                errorElement: <RouteErrorBoundary />,
              },
            ],
          },
        ],
      },
      ...(import.meta.env.DEV
        ? [
            {
              path: 'poc/reveal',
              element: (() => {
                const GsapStagedReveal = React.lazy(() =>
                  import('../features/reveal/__poc__/GsapStagedReveal').then(
                    (m) => ({ default: m.GsapStagedReveal }),
                  ),
                );
                return <GsapStagedReveal />;
              })(),
              errorElement: <RouteErrorBoundary />,
            },
          ]
        : []),
      ...(import.meta.env.DEV
        ? [
            {
              path: 'dev/shadcn',
              element: (() => {
                const ShadcnSmoke = React.lazy(() =>
                  import('../routes/dev/ShadcnSmoke').then((m) => ({
                    default: m.ShadcnSmoke,
                  })),
                );
                return <ShadcnSmoke />;
              })(),
              errorElement: <RouteErrorBoundary />,
            },
          ]
        : []),
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
];
